import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SiteNav from "./SiteNav";
import { Link } from "react-router-dom";

gsap.registerPlugin(ScrollToPlugin, ScrollTrigger);

/* -------------------- DATA -------------------- */
const GROUPS = [
  {
    id: "about",
    title: "ABOUT DOPESHOT",
    faqs: [
      {
        q: "What is DOPESHOT?",
        a: `DOPESHOT is a high-calorie endurance fuel for serious training and racing. It is a multi-carb, gut-friendly formula that delivers steady energy without upsetting your stomach. It is sold in a 3.25 pound bag in powder form with 14 servings per bag.`
      },
      {
        q: "Who is DOPESHOT for?",
        a: `Cyclists, runners, triathletes, and endurance athletes who want reliable fuel that keeps them performing longer without stomach distress.
Athletes in other sports can also benefit from its high-calorie, easy-to-consume formula. For example, in sports like MMA or tennis, where breaks are short, you need quick calories without overloading your stomach with water before intense intervals. DOPESHOT provides that balance of fuel and comfort.`
      },
      {
  q: "What makes DOPESHOT different from other fuels?",
  a: `DOPESHOT delivers up to 1,200 calories in one 20 oz bottle using maltodextrin, glucose, fructose, and sucrose — a multi-carb blend that helps your body absorb more energy per hour than single-carb fuels.
    Unlike gels or sports drinks, DOPESHOT gives you better control over how you fuel.
        • No sticky mess: Mix once, sip through the bottle, and stay focused on your training/racing. No ripping packets. No sticky fingers. No mid-race chaos. Just smooth fuelling that works when you do and allows you to focus on your training and racing.
        • More fuel per serving: Gels and drinks give you 100–200 calories; DOPESHOT can reach 1,200 in a bottle. This allows you to mix a bottle, get the calories without having to formulate a plan to get a gel, stop for more bottles or stop to mix powders on the side of the road.
        • Steady energy: No sugar spikes or gut crashes.
        • Customisable: Adjust scoops and water based on your training or race needs.`
},
      {
        q: "Why do endurance athletes need multiple carbs?",
        a: `Different carbohydrates use different absorption pathways in the gut. These pathways can reach their limit when carbohydrate intake is pushed too high. The maximum absorption rate for maltodextrin alone is around 65 grams per hour.
We include glucose, fructose, and sucrose along with maltodextrin to ensure your gut can utilize up to 100 grams of DOPESHOT per hour. Multiple carbohydrate sources mean multiple absorption pathways, allowing your body to access 100 grams per hour of usable energy.`
      },
     /* {
        q: "How should I start using it?",
        a: `Your goal should be around 100 grams of carbohydrates per hour. Two scoops equal 100 grams.
If I’m doing a four-hour Saturday morning ride, I’ll put eight scoops (400 grams) in my bottle, fill it with water, and sip on it throughout the ride.
If I’m going on a two-hour recovery ride the next day, I’ll put four scoops (200 grams) in my bottle, fill it with water, and sip on it throughout the ride.
You may not finish the entire bottle, but the goal is to train your gut to handle the intake. This is what we call “gut training.”
Dope Shot is not designed to exceed 100 grams per hour due to its carbohydrate makeup. If you want to go beyond that and reach 120 grams per hour, supplement each hour with a gel, sports drink, or even juice and water in your second bottle to hit that upper limit. The extra 20 grams should not come from maltodextrin.`
      },*/
      {
        q: "Where can I buy DOPESHOT?",
        a: `Directly from our website for the freshest stock and exclusive offers.`
      }
    ]
  },
  {
    id: "science",
    title: "THE SCIENCE BEHIND IT",
    faqs: [
      {
        q: "Why does DOPESHOT cap absorption at 120 grams of carbs per hour, and why only 100 grams per hour from Dope Shot?",
        a: `Research shows that 120 grams of carbohydrates per hour is the upper limit of what trained athletes can metabolize efficiently. Beyond this point, the gut can’t process the extra carbs into usable energy. In other words, even if you train your body to consume more than 120 grams per hour, your system won’t be able to utilize the excess, and performance will not improve with additional intake.
Dope Shot is designed to be consumed at a maximum of 100 grams per hour due to its specific carbohydrate blend. If you want to go beyond 100 grams per hour, supplement the extra 20 grams with gummies, gels, or sports drinks that are not maltodextrin-based.`
      },
      {
        q: "What is gut training and why is it important?",
        a: `Gut training teaches your stomach to handle higher carbohydrate loads during exercise. Over 10 to 16 weeks, your body adapts to absorb more fuel with less discomfort.`
      },
      {
        q: "How do I train my gut using DOPESHOT?",
        a: `Gradually increase your carbohydrate intake per hour each week until you reach your maximum, not exceeding 120 grams per hour. Most athletes will fall between 80 and 120 grams per hour.`
      },
      {
        q: "What happens if I exceed my gut’s carb limit?",
        a: `You may experience bloating, cramping, and vomiting. None of these are fun. It’s best to start low and increase slowly over 10 to 16 weeks. It’s called gut training for a reason, make your mistakes in training so that when it comes to racing, you’re not testing it out and ruining your race.`
      },
      {
        q: "Does protein help endurance performance?",
        a: `Not during exercise. Research shows that protein doesn’t enhance aerobic performance mid-race. It’s best used for recovery after your session.`
      }
    ]
  },
  {
    id: "howto",
    title: "MIXING & USING DOPESHOT",
    faqs: [
      {
        q: "How many scoops do I need for my session? / How do I start using Dope Shot?",
        a: `Each serving is 2 scoops and contains 100 grams of carbohydrates (400 calories). Start with a goal of 50 grams per hour. Increase by 5 grams per hour each week. If you experience any stomach distress, stay at your current level for a week or two before increasing again. Within 10 to 16 weeks, you’ll have a good idea of what you can comfortably consume without gut issues.`
      },
      {
        q: "What if I'm a bigger or smaller person? Should I consume more or less?",
        a: `Studies show that a person’s size and weight have no correlation with their ability to metabolize carbohydrates. You may weigh 120 pounds and still be able to handle 120 grams per hour, or you may weigh 200 pounds and only tolerate 80 grams per hour. Everyone’s gut capacity is different, which is why training your gut is so important.`
      },
      {
        q: "How should I mix it?",
        a: `Combine your chosen scoop amount with water, shake well, and sip throughout training. Avoid chugging to maintain steady absorption. If some of the Dope Shot does not disperse or clumps up, do not worry, it will dissolve on its own without any extra shaking.`
      },
      {
        q: "Do I need to drink water during training?",
        a: `Yes. Your carbohydrate needs are only one part of your performance, as maintaining blood sugar is important, but hydration is just as vital for maximum performance.
Dope Shot is not intended to be a hydration product. It is a fuel source, not a hydration source. Your other bottle should be used for hydration. The addition of a sports drink, fruit juice and water, or just water is necessary for any activity lasting longer than one hour.`
      },
      {
        q: "Can I combine DOPESHOT with other products?",
        a: `You can, but it is not necessary. DOPESHOT is complete on its own for up to 100 grams of carbohydrates per hour. if you choose to add gels, gummies, caffeine or additional electrolytes with Dope Shot,  please test it during training`
      },
      {
        q: "When should I use DOPESHOT?",
        a: `Use DOPESHOT during training, races, or as pre-race fuel.`
      }
    ]
  },
  {
    id: "ingredients",
    title: "INGREDIENTS AND NUTRITION",
    faqs: [
      { q: "What ingredients are in DOPESHOT?", a: `Maltodextrin, glucose, sucrose, fructose, citric acid, sea salt and love.` },
      { q: "Is it vegan or gluten-free?", a: `Yes. DOPESHOT is 100% vegan and gluten-free.` },
      { q: "Does it contain caffeine or stimulants?", a: `No. DOPESHOT provides steady, natural energy from carbohydrates only.` },
      { q: "How much sodium does it have?", a: `Each serving contains 300 milligrams of sodium to support muscle function.` },
      { q: "Does DOPESHOT contain fat or protein?", a: `No. It is pure carbohydrate energy designed for endurance performance.` },
      { q: "Does it use artificial flavours or colours?", a: `No.` }
    ]
  }
];


/* -------------------- UI bits -------------------- */
function Arrow({ open }) {
  return (
    <svg
      className="faq1-arrow"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M6 9l6 6 6-6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function FAQItem({ q, a, index }) {
  const [open, setOpen] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    gsap.killTweensOf(el);

    if (open) {
      const h = el.scrollHeight;
      gsap
        .timeline()
        .fromTo(
          el,
          { height: 0 },
          {
            height: h,
            duration: 0.35,
            ease: "power2.out",
            onComplete: () => (el.style.height = "auto"),
          }
        )
        .fromTo(
          el.querySelectorAll("p, span"),
          { y: 6, autoAlpha: 0 },
          { y: 0, autoAlpha: 1, stagger: 0.01, duration: 0.18, ease: "power2.out" },
          "-=0.15"
        );
    } else {
      const h = el.scrollHeight || 0;
      gsap.fromTo(el, { height: h }, { height: 0, duration: 0.28, ease: "power2.inOut" });
    }
  }, [open]);

  return (
    <div className={`faq1-item${open ? " open" : ""}`}>
      <button
        className="faq1-q"
        aria-expanded={open}
        aria-controls={`faq1-a-${index}`}
        onClick={() => setOpen(!open)}
      >
        <span className="faq1-qtxt">{q}</span>
        <Arrow open={open} />
      </button>
      <div id={`faq1-a-${index}`} ref={bodyRef} className="faq1-a" role="region" aria-hidden={!open}>
         <p>
    {a.split("\n").map((raw, i) => {
      const line = raw.trim();
      if (!line) return <br key={`br-${i}`} />;

      const isBullet = line.startsWith("•");
      const text = isBullet ? line.replace(/^•\s*/, "") : line;

      // Bold the label before the first colon
      const match = text.match(/^([^:]+:)\s*(.*)$/);
      return (
        <span key={i} className="faq-line">
          {isBullet && <span className="faq-bullet" aria-hidden>•</span>}
          {match ? (
            <>
              <strong className="faq-label">{match[1]}</strong>
              {match[2] ? ` ${match[2]}` : null}
            </>
          ) : (
            text
          )}
          <br />
        </span>
      );
    })}
  </p>
        {/*<p>{a.split("\n").map((line, i) => <span key={i}>{line}<br/></span>)}</p>*/}
      </div>
    </div>
  );
}

/* Split a string into spans for letter animations */
const splitChars = (txt) =>
  txt.split("").map((c, i) => (
    <span className="gchar" key={i}>
      {c === " " ? "\u00A0" : c}
    </span>
  ));

/* -------------------- PAGE -------------------- */
export default function FAQ1() {
  const rootRef = useRef(null);       // scope for gsap.context
  const landingRef = useRef(null);
  const sectionsRef = useRef({});
  const navRowRef = useRef(null);
  const navRef = useRef(null);
  const [activeId, setActiveId] = useState(GROUPS[0].id);
  const lastY = useRef(0);

  const navOffset = () => {
    const v = getComputedStyle(document.documentElement).getPropertyValue("--navH");
    const px = parseInt(v || "72", 10) || 72;
    return px + 12;
  };

  /*const jump = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - navOffset();
    gsap.to(window, { duration: 0.8, ease: "power3.out", scrollTo: y });
  };*/
  const jump = (id) => {
  const el = document.getElementById(id);
  if (!el) return;

  const y = el.getBoundingClientRect().top + window.scrollY - navOffset();

  // Refresh measurements before the jump so ST has correct starts/ends
  ScrollTrigger.refresh();

  gsap.to(window, {
    duration: 0.8,
    ease: "power3.out",
    scrollTo: y,
    onComplete: () => {
      // Refresh again after landing (fonts/images/sticky nav can shift layout)
      ScrollTrigger.refresh();

      // If we landed past the group's start, ensure its TL is revealed
      ScrollTrigger.getAll().forEach((st) => {
        if (st.vars && st.vars.trigger === el) {
          if (st.isActive || st.progress > 0) {
            st.animation?.progress(1).pause();
          }
        }
      });
    }
  });
};

  /* Landing animations (scoped), parallax */
  useEffect(() => {
    const ctx = gsap.context(() => {
      const root = landingRef.current;
      if (!root) return;

      // reset any inline leftovers (coming back to page)
      gsap.set(root.querySelectorAll(".faq-media img, .faq-panel, .faq-title .line, .faq-copy, .faq-link"), {
        clearProps: "all",
      });

      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
      tl.from(root.querySelector(".faq-media img"), { scale: 1.06, xPercent: -2, autoAlpha: 0, duration: 0.8 })
        .from(root.querySelector(".faq-panel"), { x: 24, autoAlpha: 0, duration: 0.6 }, "-=0.5")
        .from(root.querySelectorAll(".faq-title .line"), { yPercent: 50, autoAlpha: 0, stagger: 0.06, duration: 0.7 }, "-=0.3")
        .from(root.querySelectorAll(".faq-copy, .faq-link"), { y: 14, autoAlpha: 0, stagger: 0.05, duration: 0.45 }, "-=0.38");

      // parallax hover
      const media = root.querySelector(".faq-media img");
      const panel = root.querySelector(".faq-panel-inner");
      const onMove = (e) => {
        const r = root.getBoundingClientRect();
        const cx = (e.clientX - r.left) / r.width - 0.5;
        const cy = (e.clientY - r.top) / r.height - 0.5;
        gsap.to(media, { x: cx * 20, y: cy * 16, duration: 0.4, ease: "power2.out" });
        gsap.to(panel, { x: cx * -10, y: cy * -8, duration: 0.4, ease: "power2.out" });
      };
      const onLeave = () => gsap.to([media, panel], { x: 0, y: 0, duration: 0.5, ease: "power3.out" });

      root.addEventListener("mousemove", onMove);
      root.addEventListener("mouseleave", onLeave);

      // cleanup by context
      return () => {
        root.removeEventListener("mousemove", onMove);
        root.removeEventListener("mouseleave", onLeave);
      };
    }, rootRef);
    return () => ctx.revert();
  }, []);

  /* Nav chips on stick, groups entrances, wrap, arrow/backtop resets — all scoped */
  useEffect(() => {
    const ctx = gsap.context(() => {
      // clear inline styles (if user navigated away and back)
      gsap.set([
        ".faq1-chip",
        ".faq1-wrap",
        ".faq1-group",
        ".faq1-item",
        ".faq1-item .faq1-arrow",
        ".faq1-backtop"
      ], { clearProps: "all" });

      // Nav chips animate when the nav actually sticks
      /*
      const navEl = document.querySelector(".faq1-nav");
      ScrollTrigger.create({
        trigger: navEl,
        start: "top 15%",
        onEnter: () => {
          navEl.classList.add("is-stuck");
          gsap.from(".faq1-chip", { y: 12, autoAlpha: 0, stagger: 0.06, duration: 0.35, ease: "power2.out" });
        },
        onEnterBack: () => {
          gsap.from(".faq1-chip", { y: 12, autoAlpha: 0, stagger: 0.06, duration: 0.35, ease: "power2.out" });
        },
        onLeaveBack: () => navEl.classList.remove("is-stuck")
      });*/

      // Wrap entrance
      /*
      gsap.from(".faq1-wrap", {
        autoAlpha: 0,
        y: 18,
        duration: 0.5,
        ease: "power2.out",
        scrollTrigger: { trigger: ".faq1-wrap", start: "top bottom-=60", once: true }
      });*/

     // Each group: alternate slide-in, title letters, items, back-to-top
gsap.utils.toArray(".faq1-group").forEach((section, i) => {
  const fromX   = i % 2 === 0 ? -48 : 48;
  const letters = section.querySelectorAll(".gchar");
  const backtop = section.querySelector(".faq1-backtop");
  const items   = section.querySelectorAll(".faq1-item");
  const arrows  = section.querySelectorAll(".faq1-item .faq1-arrow");

  // make sure nothing is stuck hidden from a previous visit
  gsap.set([arrows, backtop], { clearProps: "all" });

  // build the timeline (PAUSED) — we'll play it from the ScrollTrigger below
  const tl = gsap.timeline({ paused: true, defaults: { ease: "power3.out" } });

  tl.from(section, { x: fromX, autoAlpha: 0, duration: 0.55 });

  if (letters.length) {
    tl.from(letters, {
      y: -28,
      rotateX: -80,
      transformOrigin: "50% 0%",
      autoAlpha: 0,
      stagger: 0.02,
      duration: 0.5,
      ease: "back.out(1.8)"
    }, "-=0.15");
  }

  if (backtop) {
    tl.from(backtop, { y: -8, autoAlpha: 0, duration: 0.35 }, "-=0.25")
      .add(() => gsap.set(backtop, { clearProps: "all" }));
  }

  tl.from(items, {
    y: 14,
    autoAlpha: 0,
    rotationX: 8,
    //transformPerspective: 700,
    transformOrigin: "50% 0%",
    stagger: 0.06,
    duration: 0.45
  }, "-=0.05");


  // Robust trigger that still reveals when there's no scroll room
  ScrollTrigger.create({
    trigger: section,
    start: "top bottom",   // as the section approaches the bottom of the viewport
    end: "bottom bottom",      // until its bottom hits the bottom of the viewport
    once: true,
    invalidateOnRefresh: true,
    onEnter: () => tl.play(),
    onEnterBack: () => tl.play(),
    onRefresh(self) {
      // If the start >= end, there's no space to scroll — just reveal and disable
      if (self.start >= self.end) {
        tl.progress(1).pause();
        self.disable();
      }
    }
  });

  // Robust trigger for each group – cannot be skipped even with fast jumps
   /* ScrollTrigger.create({
      trigger: section,
      start: "top 98%",     // as soon as the group's top reaches the bottom of the viewport
      end: "+=1",           // guarantees end > start (even for tiny sections)
      once: true,
      invalidateOnRefresh: true,
      immediateRender: false,
      onEnter: () => tl.play(),
      onEnterBack: () => tl.play(),
      onRefresh(self) {
        // If the page loads/jumps and we’re already past start, just reveal
        if (self.isActive || self.progress > 0) {
          tl.progress(1).pause();
        }
      }
    });*/
    
        // IMPORTANT: animate arrows WITHOUT transform (so CSS rotate on .open works)
        /*
        tl.from(arrows, {
          autoAlpha: 0,
          y: -8,
          stagger: 0.05,
          duration: 0.35,
          ease: "back.out(2)"
        }, "<0.05")
        .add(() => gsap.set(arrows, { clearProps: "opacity,transform" })); // fully hand control back to CSS*/
      });
    }, rootRef);

    return () => ctx.revert(); // kills triggers & restores inline styles
  }, []);

  /* Scroll spy + progress + smart hide/show of sticky nav */
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const most = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (most?.target?.id) setActiveId(most.target.id);
      },
      { rootMargin: `-${(parseInt(getComputedStyle(document.documentElement).getPropertyValue("--navH")) || 72) + 20}px 0px -52% 0px`, threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    GROUPS.forEach((g) => {
      const el = document.getElementById(g.id);
      if (el) obs.observe(el);
    });

    let raf = 0;
    const update = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - doc.clientHeight;
      const p = max > 0 ? ((doc.scrollTop || 0) / max) * 100 : 0;
      document.documentElement.style.setProperty("--faq1Progress", `${p}%`);

      const y = window.scrollY || 0;
      const nav = document.querySelector(".faq1-nav");
      if (nav && nav.classList.contains("is-stuck")) {
        const goingDown = y > lastY.current + 6;
        const goingUp = y < lastY.current - 6;
        if (goingDown && y > 140) nav.setAttribute("data-hidden", "true");
        if (goingUp) nav.setAttribute("data-hidden", "false");
      }
      lastY.current = y;
      raf = 0;
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
      obs.disconnect();
    };
  }, []);

  // Auto-center the active chip in the horizontal scroller
useEffect(() => {
  const row = navRowRef.current;
  if (!row) return;
  const btn = row.querySelector('.faq1-chip[aria-current="true"]');
  if (!btn) return;

  const target =
    btn.offsetLeft - (row.clientWidth - btn.clientWidth) / 2;

  gsap.to(row, {
    duration: 0.4,
    ease: "power2.out",
    scrollTo: { x: target, autoKill: true }
  });
}, [activeId]);

useEffect(() => {
  const refresh = () => ScrollTrigger.refresh();
  window.addEventListener("load", refresh);
  gsap.delayedCall(0, refresh);
  return () => window.removeEventListener("load", refresh);
}, []);

  return (
    <div className="page" ref={rootRef}>
      <SiteNav />

      {/* Landing */}
      <section ref={landingRef} className="faq-landing" id="faq-landing" aria-label="FAQ landing">
        <div className="faq-media" aria-hidden>
          <img src="/DSCF9560.webp" alt="DOPESHOT being poured into a bottle" loading="eager" />
          <span className="faq-grain" aria-hidden />
        </div>

        <div className="faq-panel">
          <div className="faq-panel-inner">
            <p className="faq-eyebrow"></p>
            <h1 className="faq-title">
              <span className="line">FREQUENTLY</span>
              <span className="line">ASKED</span>
              <span className="line">QUESTIONS</span>
            </h1>
            <p className="faq-copy">Find straight answers about <strong>DOPESHOT</strong>. <br/>Learn what it is, how it works, and <br/>how to fuel smarter for longer endurance performance.</p>
            <p className="faq-copy">Didn’t find the answer you’re looking for?</p>
            <p className="faq-copy">
              <Link className="faq-link" to="/contact">Contact us</Link>
            </p>
          </div>

          <div className="faq-bars" aria-hidden>
            <span className="top" />
            <span className="bot" />
          </div>
          <span className="faq-shine" aria-hidden />
        </div>
      </section>

      {/* Sticky jump buttons */}
      <nav className="faq1-nav" aria-label="FAQ sections">
        <div className="faq1-progress" aria-hidden="true" />
        <div className="faq1-nav-row" ref={navRowRef}>
          {GROUPS.map((g) => (
            <button
              key={g.id}
              className="faq1-chip"
              onClick={() => jump(g.id)}
              aria-current={activeId === g.id ? "true" : "false"}
              style={{textTransform: "Uppercase"}}
            >
              {g.title}
            </button>
          ))}
        </div>
      </nav>

      {/* Groups */}
      <main className="faq1-wrap" id="faq-groups">
        {GROUPS.map((group, gi) => (
          <section
            key={group.id}
            id={group.id}
            className="faq1-group"
            aria-labelledby={`${group.id}-title`}
            ref={(n) => (sectionsRef.current[group.id] = n || undefined)}
          >
            <div className="faq1-titlebar">
              <h2 id={`${group.id}-title`} className="faq1-group-title"  style={{textTransform: "Uppercase"}}>
                <span className="glow" />
                <span className="gchars">{splitChars(group.title)}</span>
              </h2>
              <button
                className="faq1-backtop"
                onClick={() => gsap.to(window, { scrollTo: 0, duration: 0.6 })}
                type="button"
              >
                ↑ Back to top
              </button>
            </div>

            <div className="faq1-list">
              {group.faqs.map((item, i) => (
                <FAQItem key={`${group.id}-${i}`} q={item.q} a={item.a} index={`${gi}-${i}`} />
              ))}
            </div>

            <hr className="hr-shine" />
          </section>
          
          
        ))}

      
      </main>

      

          {/* Bottom contact */}
        <section className="faq1-contact">
          <h3>Still have questions?</h3>
          <p>Send us a message anytime, <Link to="/contact">let's talk</Link>!</p>
        </section>
      {/* Footer */}
      <footer className="site-footer">
        <div className="foot-inner">
          <Link to="/">Terms</Link>
          <Link to="/">Privacy</Link>
          <Link to="/">Support</Link>
        </div>
        <div className="foot-copy">© Dope Shot</div>
      </footer>
    </div>
  );
}
